import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import breadcrumbs from '@/stores/breadcrumbModule';
import notifications from '@/stores/notificationModule';
import appModules from '@/stores/appModule';
import groupLevels from '@/stores/groupLevelsModule';
import lookups from '@/stores/lookupsModule';
import propertiesModule from '@/stores/propertiesModule';
import fieldLabelsModule from '@/stores/fieldLabelsModule';
import personSearchModule from '@/stores/personSearchModule';
import { version } from '../package.json';

Vue.use(Vuex);

const districtReducer = (obj, curr) => ({
  ...obj,
  [curr.code]: curr,
});

const schoolReducer = (obj, curr) => ({
  ...obj,
  [`${curr.code}-${curr.districtCode}`]: curr,
});

const locationGroupReducer = (obj, curr) => ({
  ...obj,
  [curr.locationGroupId]: curr,
});

const buildPermissionList = (locationPermissions) => {
  const list = locationPermissions.map((lp) => {
    const districts = lp.districts.map(d => d.code);
    const schools = lp.schools.map(s => s.code);
    const programs = lp.programs.map(p => p.programCode);
    const { permissions } = lp;

    return {
      districts,
      schools,
      programs,
      permissions,
    };
  });

  return list;
};

export const config = {
  modules: {
    breadcrumbs,
    notifications,
    appModules,
    groupLevels,
    lookups,
    propertiesModule,
    fieldLabelsModule,
    personSearchModule,
  },
  state: {
    header: '',
    user: null,
    permissions: null,
    navExpanded: false,
    sidebarOpen: false,
    sessionWarning: false,
    sessionTimer: null,
    profileDropDownOpen: false,
    timeout: null, // length of the user session as defined by the server, set on login
    userPersonTypes: [],
    version,
  },
  getters: {
    CEPElectionApprovalUser: state => state.user.locationPermissions
      .some(lp => lp.permissions.includes('DRMCH:CEP:ELECTIONS:APPROVE')),
    CEPLEAListUser: state => state.user.locationPermissions
      .some(lp => lp.permissions.includes('DRMCH:CEP:MGR:LEA:VIEW')),
    CEPPopulationApprovalUser: state => state.user.locationPermissions
      .some(lp => lp.permissions.includes('DRMCH:CEP:POPULATION:APPROVE')),
    hasAllGroupAccess: (state, getters) => !getters.userLocationGroups.length,
    hasDistrictSchoolPermission: state => ({
      permission, districtCode, schoolCode,
    }) => state.permissions.some((perm) => {
      const hasDistrict = perm.districts.includes(districtCode) || perm.districts.length === 0;
      const hasSchool = perm.schools.includes(schoolCode) || perm.schools.length === 0;
      const hasPermission = perm.permissions.includes(permission);

      return hasDistrict && hasSchool && hasPermission;
    }),
    hasProgramPermission: state => ({
      permission, districtCode, schoolCode, programCode,
    }) => state.permissions.some((perm) => {
      const hasDistrict = perm.districts.includes(districtCode) || perm.districts.length === 0;
      const hasSchool = perm.schools.includes(schoolCode) || perm.schools.length === 0;
      const hasProgram = perm.programs.includes(programCode);
      const hasPermission = perm.permissions.includes(permission);

      return hasDistrict && hasSchool && hasProgram && hasPermission;
    }),
    hasSpecificGroupAccess: (state, getters) => (!getters.hasAllGroupAccess
      ? !getters.userLocationGroups.some(g => g.locationGroupId === -1) : false),
    isDistrictUser: (state, getters) => getters.userDistricts.length && !getters.userSchools.length,
    isMultiSchoolUser: (state, getters) => getters.userDistricts.length === 1
      && getters.userSchools.length > 1,
    isSingleDistrictUser: (state, getters) => getters.userDistricts.length === 1,
    isSingleSchoolUser: (state, getters) => getters.userSchools.length === 1,
    isStateUser: (state, getters) => !getters.userDistricts.length,
    permissions: state => state.user.permissions,
    sessionWarning: state => state.sessionWarning,
    sidebarOpen: state => state.sidebarOpen,
    user: state => state.user,
    userPersonTypes: state => state.user.userPersonTypes
      .map(item => item.personType)
      .filter(a => a.status === true),
    userDistricts: (state) => {
      const allDistricts = state.user.locationPermissions.map(lp => lp.districts).flat();
      return Object.values(allDistricts.reduce(districtReducer, {}));
    },
    userLocationGroups: (state) => {
      const allLocationGroups = state.user.locationPermissions.map(lp => lp.locationGroups).flat();
      return Object.values(allLocationGroups.reduce(locationGroupReducer, {}));
    },
    userSchools: (state) => {
      const allSchools = state.user.locationPermissions.map(lp => lp.schools).flat();
      return Object.values(allSchools.reduce(schoolReducer, {}));
    },
  },
  mutations: {
    SET_HEADER: (state, header) => {
      Vue.set(state, 'header', header);
    },
    SET_FOOTER: (state, footer) => {
      Vue.set(state, 'footer', footer);
    },
    SET_SIDEBAR_STATE: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    SET_PROFILE_DROPDOWN_STATE: (state) => {
      state.profileDropDownOpen = !state.profileDropDownOpen;
    },
    COLLAPSE_PROFILE_DROPDOWN_STATE: (state) => {
      state.profileDropDownOpen = false;
    },
    SET_USER_PROFILE: (state, u) => {
      Vue.set(state, 'user', u);
      Vue.set(state, 'permissions', buildPermissionList(u.locationPermissions));
    },
    REMOVE_USER_PROFILE: (state) => {
      Vue.delete(state, 'user');
      Vue.delete(state, 'permissions');
    },
    REMOVE_SESSION_WARNING(state) {
      Vue.set(state, 'sessionWarning', false);
    },
    REMOVE_SESSION_TIMER(state) {
      Vue.delete(state, 'sessionTimer');
    },
    SET_TIMEOUT(state, timeout) {
      state.timeout = timeout;
    },
  },
  actions: {
    /**
     * Makes a call to retrieve an active session
     */
    async initSession({ dispatch }) {
      const response = await axios.get('/session');
      const { data: userSession } = response;
      return dispatch('configureSession', userSession);
    },
    async initBranding({ dispatch }) {
      const response = await axios.get('/branding');
      const { data } = response;
      return dispatch('configureBranding', data);
    },
    /**
     * Setup a user session in the store
     */
    async configureSession({ commit }, { user, timeout }) {
      commit('personSearchModule/RESET_ALL_SEARCH_STATE', null, { root: true });
      const u = user;
      u.permissions = user.locationPermissions.map(lp => lp.permissions).flat();
      commit('SET_USER_PROFILE', u);
      commit('SET_TIMEOUT', timeout);
      return u;
    },
    async configureBranding({ commit }, { header, footer }) {
      commit('SET_HEADER', header);
      commit('SET_FOOTER', footer);
    },
    toggleSidebar({ commit }) {
      commit('SET_SIDEBAR_STATE');
    },
    toggleProfileDropDown({ commit }) {
      commit('SET_PROFILE_DROPDOWN_STATE');
    },
    collapseProfileDropDown({ commit }) {
      commit('COLLAPSE_PROFILE_DROPDOWN_STATE');
    },
    async validateLogin({ dispatch }, payload) {
      const response = await axios.post('/login', payload);
      const { data: userSession } = response;
      await dispatch('configureSession', userSession);
      return userSession.user;
    },

    async validateSsoLogin({ dispatch }, { params, accessToken }) {
      console.log(`SSO-Message: validateSsoLogin() - making user-authentication api call..., params=${params}, accessToken=${accessToken}`);

      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
      };

      // If accessToken is defined and has a value, then set the access-token
      if (accessToken) {
        headers.Authorization = accessToken;
      }
      const options = {
        method: 'POST',
        headers,
        params,
        url: '/login/sso',
      };
      const response = await axios(options);
      const { data: userSession } = response;
      await dispatch('configureSession', userSession);
      return userSession.user;
    },
    lostPassword(context, payload) {
      return axios.post('/login/lostpassword', payload);
    },
    async resetPassword({ dispatch }, payload) {
      const response = await axios.post('/login/resetpassword', payload);
      const { data: userSession } = response;
      await dispatch('configureSession', userSession);
      return userSession;
    },
    verifyToken(context, token) {
      return axios.get(`/login/verifytoken?token=${token}`);
    },
    verifyEmail(context, payload) {
      return axios({
        method: 'post',
        url: '/login/verifyemail',
        data: payload,
      });
    },
    async signup({ dispatch }, payload) {
      const response = await axios.post('/login/signup', payload);
      const { data: userSession } = response;
      await dispatch('configureSession', userSession);
      return userSession.user;
    },
    getTransaction(context, transactionId) {
      return axios.get(`/directmatch/transactions/${transactionId}`);
    },
    getPersonTypes() {
      return axios.get('/person-types');
    },
    getRoles() {
      return axios.get('/roles?active=true');
    },
    getRole(context, id) {
      return axios.get(`/roles/${id}`);
    },
    getRaces() {
      console.warn('deprecated lookup API called for \'race\'');
      return axios.get('/race?active=true');
    },
    getPrivileges(context, id) {
      return axios.get(`/roles/${id}/privileges`);
    },
    getLocationGroups() {
      return axios.get('/location-groups/lookup?active=true');
    },
    getLocationPermissions(context, id) {
      return axios.get(`/users/${id}/locationpermissions`);
    },
    addUser(context, payload) {
      return axios({
        method: 'post',
        url: '/users',
        data: payload,
      });
    },
    getUserProfiles(context, payload) {
      return axios.get(`/users?${payload}`);
    },
    forceResetPassword(context, id) {
      return axios.post(`/users/${id}/reset-password`, {});
    },
    resendSignupEmailForUser(context, id) {
      return axios.post(`/users/${id}/resend-signup`, {});
    },
    getProfile(context, id) {
      return axios.get(`/users/${id}?profile=true`);
    },
    getStates() {
      console.warn('deprecated lookup API called for \'states\'');
      return axios.get('/states?active=true');
    },
    getTitles() {
      return axios.get('/titles?active=true');
    },
    updateUserPw(context, payload) {
      return axios({
        method: 'put',
        url: '/users/changepw',
        data: payload,
      });
    },
    updateUserPT(context, payload) {
      return axios({
        method: 'put',
        url: `/users/${payload.id}/person-types`,
        data: payload.userPersonTypes,
      });
    },
    updateUser(context, payload) {
      return axios({
        method: 'patch',
        url: `/users/${payload.id}`,
        data: payload.user,
      });
    },
    async signOff(context) {
      context.commit('REMOVE_SESSION_WARNING');
      context.commit('personSearchModule/RESET_ALL_SEARCH_STATE', null, { root: true });
      context.commit('REMOVE_USER_PROFILE');
      context.commit('propertiesModule/CLEAR_PROPERTIES');
      return axios.post('/session/signoff', {}).then(() => {
        context.commit('REMOVE_SESSION_TIMER');
      });
    },
    getDistrict(context, code) {
      return axios.get(`/districts/${code}`);
    },
    getSchool(context, payload) {
      return axios.get(`/districts/${payload.districtCode}/schools/${payload.schoolCode}`);
    },
    getLocation(context, id) {
      return axios.get(`/locations/${id}`);
    },
    getLocationProfile(context, id) {
      console.warn('deprecated lookup API called for \'LocationProfile\'');
      return axios.get(`/locations/${id}/profile`);
    },
    getStudent(context, studentId) {
      return axios.get(`/person/${studentId}`);
    },
    getSources() {
      console.warn('deprecated lookup API called for \'source\'');
      return axios.get('/sources?active=true');
    },
    getGenders() {
      console.warn('deprecated lookup API called for \'gender\'');
      return axios.get('/genders?active=true');
    },
    updateLocationOrganization(context, payload) {
      return axios({
        method: 'put',
        url: `/locations/${payload.locationOrganization.stateLocationId}/organizations/${payload.locationOrganization.organizationCode}?panel=${payload.panel}`,
        data: payload.locationOrganization,
      });
    },
    updateLocationGrades(context, payload) {
      return axios({
        method: 'post',
        url: `/locations/${payload.stateLocationId}/grades`,
        data: payload.locationGrades,
      });
    },
    updateLocationFacilities(context, payload) {
      return axios({
        method: 'post',
        url: `/locations/${payload.stateLocationId}/facilities`,
        data: payload.locationFacilities,
      });
    },
    updateLocationAttributes(context, payload) {
      return axios({
        method: 'post',
        url: `/locations/${payload.stateLocationId}/attributes`,
        data: payload.locationAttributes,
      });
    },
    getLocationOrganization(context, id) {
      return axios.get(`/locations/${id}/organizations`);
    },
    getLocationHistory(context, payload) {
      return axios.get(`/locations/${payload.id}/history`,
        { params: payload.params });
    },
    getLocationOrganizationHistory(context, payload) {
      return axios.get(`/locations/${payload.id}/organizations/${payload.code}/history`,
        { params: payload.params });
    },
    updateLocation(context, payload) {
      return axios({
        method: 'put',
        url: `/locations/${payload.location.stateLocationId}`,
        data: payload.location,
      });
    },
    getlocationGroupMapping(context, id) {
      return axios.get(`/locations/${id}/location-groups`);
    },
    getPrograms() {
      return axios.get('/programs?active=true');
    },
    getMatchTypes() {
      console.warn('deprecated lookup API called for \'match types\'');
      return axios.get('/matchtypes?active=true');
    },
    extendSession(context) {
      context.commit('REMOVE_SESSION_WARNING');
      return axios.get('/session');
    },
  },
};

export default new Vuex.Store(config);
